export type BillingErrorMeta = {
  upgrade_to?: string | null;
  required?: number;
  available?: number;
};

/** Error thrown for any failed API response. Carries the backend error `code`
 * and `meta` so callers can surface a plan upgrade CTA on 402 blocks
 * (feature_locked / resource_limit / insufficient_tokens). Extends Error, so
 * existing `err instanceof Error` handling keeps working unchanged. */
export class BillingError extends Error {
  code?: string;
  meta?: BillingErrorMeta;
  status: number;
  constructor(message: string, opts: { code?: string; meta?: BillingErrorMeta; status: number }) {
    super(message);
    this.name = "BillingError";
    this.code = opts.code;
    this.meta = opts.meta;
    this.status = opts.status;
  }
}

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshAt = 0;

/** Treat a refresh that landed within this window as still current.
 *
 * A page like Settings mounts a dozen queries at once, so an expired access
 * token produces a dozen simultaneous 401s. The in-flight dedupe below catches
 * the ones that overlap, but a request that 401'd against the *old* token and
 * resolved just after the rotation completed would fire a second refresh
 * carrying a token the server had already retired, and get the whole session
 * revoked for token reuse. Requests arriving just behind a successful refresh
 * simply retry with the new cookie instead. */
const REFRESH_FRESH_MS = 5_000;

async function refreshSession(): Promise<boolean> {
  if (Date.now() - lastRefreshAt < REFRESH_FRESH_MS) return true;
  if (!refreshPromise) {
    refreshPromise = fetch("/api/auth/refresh", { method: "POST", credentials: "include" })
      .then((res) => {
        if (res.ok) lastRefreshAt = Date.now();
        return res.ok;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/** Pull the REAL reason out of a failed response for the raw-`fetch` call sites
 * (streaming chat, TTS preview, file uploads) that can't go through `api()`.
 *
 * A bare "failed (404)" tells nobody anything, not the user, not us. So we read
 * the envelope's `error.message`, fall back to a plain-text body, and only then
 * to the status line. The status code is always appended so a report still
 * carries it. */
export async function readErrorMessage(res: Response, fallback = "Request failed"): Promise<string> {
  const status = `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
  let detail = "";
  try {
    const text = await res.text();
    if (text) {
      try {
        const body = JSON.parse(text);
        detail = body?.error?.message || body?.message || body?.detail || "";
        if (!detail && typeof body?.error === "string") detail = body.error;
      } catch {
        detail = text.slice(0, 400);
      }
    }
  } catch {
    /* body already consumed or unreadable, fall through to the status line */
  }
  detail = (detail || "").trim();
  return detail ? `${detail} (${status})` : `${fallback} (${status})`;
}

export async function api<T = unknown>(path: string, init?: RequestInit, _retried = false): Promise<T> {
  // Tenancy is resolved by the backend from the caller's membership, there is
  // no client-side org override: a platform super admin holds no membership and
  // is never allowed inside a tenant.
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers || {}),
    },
    credentials: "include",
  });

  if (res.status === 401 && !_retried && path !== "/auth/refresh") {
    const refreshed = await refreshSession();
    if (refreshed) {
      return api<T>(path, init, true);
    }
    // The session is genuinely gone, it expired, the user signed out
    // everywhere, or an admin ended this device's session. Leaving them on a
    // page that quietly fails every request reads as "the product is broken",
    // so send them to sign in and say why.
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login?reason=expired";
    }
  }

  const body = await res.json().catch(() => ({ success: false, error: { message: res.statusText } }));
  if (!res.ok || !body.success) {
    throw new BillingError(body.error?.message || `Request failed (${res.status})`, {
      code: body.error?.code,
      meta: body.meta,
      status: res.status,
    });
  }
  return body.data as T;
}
